import Cookies from 'js-cookie';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useVerifyEmailChangeMutation } from '@app/api/User/api';
import {
  BY_HELP_AUTH,
  BY_HELP_EMAIL,
  DEV_BY_HELP_AUTH,
  DEV_BY_HELP_EMAIL,
  PROD_MODE,
} from '@app/constants';
import { handleCatchError } from '@app/services';
import { setAuthReset, useAppDispatch } from '@app/store';
import { defineDeployingMode } from '@app/utils';

export const useEmailChange = () => {
  const [isLoader, setLoader] = useState(true);
  const [isEmailChanged, setIsEmailChanged] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [serverError, setServerError] = useState('');

  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const verifyCode = searchParams.get('verifyCode');

  const dispatch = useAppDispatch();
  const [verifyEmailChange] = useVerifyEmailChangeMutation();

  const deleteAuthorizationCookie = useCallback(() => {
    const authCookieName =
      defineDeployingMode(window.location.host) === PROD_MODE
        ? BY_HELP_AUTH
        : DEV_BY_HELP_AUTH;
    const emailCookieName =
      defineDeployingMode(window.location.host) === PROD_MODE
        ? BY_HELP_EMAIL
        : DEV_BY_HELP_EMAIL;

    Cookies.remove(authCookieName);
    Cookies.remove(emailCookieName);
    dispatch(setAuthReset());
  }, [dispatch]);

  useEffect(() => {
    const startVerifyingEmailChange = async () => {
      if (verifyCode) {
        try {
          setLoader(true);

          const { Email } = await verifyEmailChange({
            code: verifyCode,
          }).unwrap();

          setIsEmailChanged(true);
          setNewEmail(Email);
          deleteAuthorizationCookie();
        } catch (error) {
          setIsEmailChanged(false);
          setServerError(handleCatchError(error));
        } finally {
          setLoader(false);
        }
      }
    };

    startVerifyingEmailChange();
  }, [verifyCode, deleteAuthorizationCookie, verifyEmailChange]);

  const navigateToNextPage = (path: string) => {
    if (isEmailChanged) {
      sessionStorage.clear();
    }
    navigate(path);
  };

  return {
    isLoader,
    isEmailChanged,
    newEmail,
    serverError,
    navigateToNextPage,
  };
};
